import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/InputDefault";
import { Textarea } from "@/components/ui/textarea";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Save,
  X,
  Edit,
  Trash2,
} from "lucide-react";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useToast } from "@/hooks/use-toast";
import {
  updateType,
  deleteType,
  createTypeElement,
} from "@/lib/stores/typesStore";
import { TypeItem } from "@/lib/stores/typesStore";
import TypeElementBadge from "./TypeElementBadge";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TypeListProps {
  types: TypeItem[];
  isLoading: boolean;
  onRefresh: () => void;
}

interface EditingType {
  name: string;
  element_type: string;
  group: string;
  description: string;
}

interface NewElement {
  name: string;
  description: string;
}

function TypeList({ types, isLoading, onRefresh }: TypeListProps) {
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();

  // Element type options
  const elementTypes = [
    "text",
    "boolean",
    "number",
    "date",
    "email",
    "url",
    "image",
    "file",
    "select",
    "multiselect",
    "textarea",
    "rich_text",
  ];

  // State for collapsible types
  const [collapsedTypes, setCollapsedTypes] = useState<Record<string, boolean>>(
    {}
  );

  // State for editing types
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [editingType, setEditingType] = useState<EditingType>({
    name: "",
    element_type: "text",
    group: "",
    description: "",
  });

  // State for adding elements
  const [isAddingElement, setIsAddingElement] = useState<string | null>(null);
  const [newElement, setNewElement] = useState<NewElement>({
    name: "",
    description: "",
  });

  // Toggle type collapse state
  const toggleType = (typeId: string) => {
    setCollapsedTypes((prev) => ({
      ...prev,
      [typeId]: !prev[typeId],
    }));
  };

  // Handle type operations
  const handleUpdateType = async (type: TypeItem) => {
    if (!selectedGame || !editingType.name.trim()) {
      toast({
        title: "Error",
        description: "Please provide a name for the type",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateType(
        {
          id: type.id,
          game_id: selectedGame.id,
          name: editingType.name.trim(),
          element_type: editingType.element_type,
          group: editingType.group.trim() || undefined,
          description: editingType.description.trim() || undefined,
          data: type.data || [],
        },
        toast
      );

      setEditingType({
        name: "",
        element_type: "text",
        group: "",
        description: "",
      });
      setEditingTypeId(null);

      toast({
        title: "Success",
        description: "Type updated successfully",
      });

      // onRefresh() removed - relying on store subscription for updates
    } catch (error) {
      console.error("Error updating type:", error);
      toast({
        title: "Error",
        description: "Failed to update type. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteType = async (typeId: string) => {
    if (!selectedGame) return;

    try {
      await deleteType(typeId, selectedGame.id, toast);

      toast({
        title: "Success",
        description: "Type deleted successfully",
      });

      // onRefresh() removed - relying on store subscription for updates
    } catch (error) {
      console.error("Error deleting type:", error);
      toast({
        title: "Error",
        description: "Failed to delete type. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle element operations
  const handleAddElement = async (typeId: string) => {
    if (!newElement.name.trim()) {
      toast({
        title: "Error",
        description: "Please provide a name for the element",
        variant: "destructive",
      });
      return;
    }

    try {
      await createTypeElement(
        {
          type_id: typeId,
          name: newElement.name.trim(),
          description: newElement.description.trim() || undefined,
        },
        toast
      );

      setNewElement({ name: "", description: "" });
      setIsAddingElement(null);

      toast({
        title: "Success",
        description: "Element added successfully",
      });

      // onRefresh() removed - relying on store subscription for updates
    } catch (error) {
      console.error("Error adding element:", error);
      toast({
        title: "Error",
        description: "Failed to add element. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Start editing functions
  const startEditingType = (type: TypeItem) => {
    setEditingTypeId(type.id);
    setEditingType({
      name: type.name,
      element_type: type.element_type,
      group: type.group_name || "",
      description: type.description || "",
    });
  };

  // Cancel editing functions
  const cancelEditingType = () => {
    setEditingTypeId(null);
    setEditingType({
      name: "",
      element_type: "text",
      group: "",
      description: "",
    });
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{t('platform.cards.widgets.loading_types')}</p>
      </div>
    );
  }

  if (types.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{t('platform.cards.widgets.no_types_created_yet')}</p>
        <p className="text-xs text-gray-400 mt-1">
          Create your first type to get started
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {types.map((type) => {
        const isTypeCollapsed = collapsedTypes[type.id] ?? true;

        return (
          <div key={type.id} className="space-y-2">
            {editingTypeId === type.id ? (
              // Editing Mode
              <div className="bg-white border border-gray-300 rounded-lg p-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    value={editingType.name}
                    onChange={(e) =>
                      setEditingType({ ...editingType, name: e.target.value })
                    }
                    placeholder={t('platform.cards.widgets.type_name')}
                    size={8}
                  />
                  <select
                    value={editingType.element_type}
                    onChange={(e) =>
                      setEditingType({
                        ...editingType,
                        element_type: e.target.value,
                      })
                    }
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {elementTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <Input
                  value={editingType.group}
                  onChange={(e) =>
                    setEditingType({ ...editingType, group: e.target.value })
                  }
                  placeholder={t('platform.cards.widgets.group')}
                  size={8}
                />
                <Textarea
                  value={editingType.description}
                  onChange={(e) =>
                    setEditingType({
                      ...editingType,
                      description: e.target.value,
                    })
                  }
                  placeholder={t('platform.common.description')}
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleUpdateType(type)}
                    className="flex items-center gap-2"
                  >
                    <Save className="h-3 w-3" />
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelEditingType}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Type Header */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => toggleType(type.id)}
                    className="flex items-center gap-2 hover:text-gray-700 transition-colors"
                  >
                    {isTypeCollapsed ? (
                      <ChevronRight className="h-3 w-3 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-3 w-3 text-gray-500" />
                    )}
                    <span className="text-sm font-medium text-gray-800">
                      {type.name}
                    </span>
                    <span className="text-xs px-2 py-0.5 border border-grey-G3 text-grey-G1  rounded">
                      {type.element_type}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({type.data?.length || 0} elements)
                    </span>
                  </button>

                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEditingType(type)}
                      className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                      title={t('platform.cards.widgets.edit_type')}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteType(type.id)}
                      className="h-6 w-6 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                      title={t('platform.cards.widgets.delete_type')}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Type Content */}
                {!isTypeCollapsed && (
                  <div className="ml-5 space-y-2">
                    {/* Add Element Button */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsAddingElement(type.id)}
                        className="h-6 text-xs"
                        disabled={isAddingElement === type.id}
                      >
                        Add Element
                      </Button>
                    </div>

                    {/* Add Element Form */}
                    {isAddingElement === type.id && (
                      <div className="bg-white border border-gray-300 rounded p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-700">
                            Add Element
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsAddingElement(null)}
                            className="h-5 w-5 p-0"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <Input
                          value={newElement.name}
                          onChange={(e) =>
                            setNewElement({
                              ...newElement,
                              name: e.target.value,
                            })
                          }
                          placeholder={t('platform.cards.widgets.element_name')}
                          size={8}
                        />
                        <Textarea
                          value={newElement.description}
                          onChange={(e) =>
                            setNewElement({
                              ...newElement,
                              description: e.target.value,
                            })
                          }
                          placeholder={t('platform.cards.widgets.element_description')}
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAddElement(type.id)}
                            className="flex items-center gap-1"
                          >
                            <Save className="h-3 w-3" />
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setIsAddingElement(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Elements List */}
                    {type.data && type.data.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {type.data.map((element) => (
                          <TypeElementBadge
                            key={element.id}
                            element={element}
                            typeId={type.id}
                            onRefresh={onRefresh}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        <p className="text-xs text-gray-500">{t('platform.cards.widgets.no_elements_yet')}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default TypeList;
